import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import Modal, { useModalContext } from '@/components/Modal/Modal';
import STORAGE_KEYS from '@/configs/local-storage-keys';
import { useStructuredStorageFilterQuery } from '@/features/StructuredFilter/hooks/useStructuredFilterQuery';
import { Table } from '@/shared/ui';
import { toast } from '@/stores/pure/ToastStore';
import { capitalizeFirstLetterInWord } from '@/utils/util';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { productInfoService } from '../../api';
import useProductStore from '../../store/ProductStore';
import type { ProductHierarchy } from '../../types';
import {
  getOptionByProduct,
  getProductByBrand,
  getProductByLine,
} from '../../utils/product.utils';

type Props = {
  target: ProductHierarchy;
  targetId: string;
  onDeleteConfirmCallback?: () => void;
};

export const DeleteModal = (props: Props) => {
  const { closeModal } = useModalContext();
  const { clearFilter } = useStructuredStorageFilterQuery(
    STORAGE_KEYS.FEEDBACK_FILTER_STATUS_KEY,
  );
  const fetchProductList = useProductStore((state) => state.fetchProductList);
  const brandMap = useProductStore((state) => state.brandMap);
  const lineMap = useProductStore((state) => state.lineMap);
  const productMap = useProductStore((state) => state.groupedProductMap);
  const productOptionMap = useProductStore((state) => state.productOptionMap);
  const queryClient = useQueryClient();
  const { target, targetId, onDeleteConfirmCallback } = props;

  const clearCache = () => {
    clearFilter();
    queryClient.invalidateQueries();
  };

  const deleteFn = useCallback(async () => {
    switch (target) {
      case 'product':
        await productInfoService.deleteProduct(targetId);
        break;
      case 'brand':
        await productInfoService.deleteBrand(targetId);
        break;
      case 'line':
        await productInfoService.deleteLine(targetId);
        break;
      case 'option':
        await productInfoService.deleteProductOption(targetId);
        break;
      default:
        throw new Error(`Invalid target: ${target}`);
    }
  }, [target, targetId]);

  const targetName = useMemo(() => {
    switch (target) {
      case 'product':
        return productMap.get(targetId)?.product_name;
      case 'brand':
        return brandMap.get(targetId)?.name;
      case 'line':
        return lineMap.get(targetId)?.name;
      case 'option':
        return productOptionMap.get(targetId)?.option_name;
      default:
        throw new Error(`Invalid target: ${target}`);
    }
  }, [brandMap, lineMap, productMap, productOptionMap, target, targetId]);

  const onDeleteConfirm = async () => {
    await deleteFn();
    toast(`${targetName} deleted`, 'success');
    await fetchProductList(getCurrWorkspaceAlias());
    await closeModal();
    clearCache();
    onDeleteConfirmCallback?.();
  };

  const childItems = useMemo(() => {
    switch (target) {
      case 'product': {
        return getOptionByProduct(productMap, targetId);
      }
      case 'brand': {
        const products = getProductByBrand(productMap, targetId);
        const options = products.flatMap((product) =>
          getOptionByProduct(productMap, product.id),
        );
        return [...products, ...options];
      }

      case 'line': {
        const products = getProductByLine(productMap, targetId);
        const options = products.flatMap((product) =>
          getOptionByProduct(productMap, product.id),
        );
        return [...products, ...options];
      }
      case 'option':
        return [];
      default:
        throw new Error(`Invalid target: ${target}`);
    }
  }, [productMap, target, targetId]);

  return (
    <Modal.Layout
      modalSize="m"
      className="max-h-[calc(100vh-64px)] overflow-auto"
    >
      <Modal.Header>
        <Modal.Title>
          Are you sure you want to delete ‘{targetName}’?
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="flex flex-col gap-4 overflow-auto">
        <span>
          Deleting this will unlink all associated feedback, and sub-items will
          be deleted. This cannot be undone.
        </span>
        {childItems && childItems.length > 0 && (
          <Table.Root>
            <Table.Header className="bg-gray-200">
              <Table.Col className="flex-1">Name</Table.Col>
              <Table.Col className="w-25">Type</Table.Col>
              <Table.Col className="w-25">Action</Table.Col>
            </Table.Header>
            {childItems.map((item) => {
              return (
                <Table.Row key={item?.id}>
                  <Table.Cell className="truncate flex-1">
                    {item?.name}
                  </Table.Cell>
                  <Table.Cell className="w-25">
                    {capitalizeFirstLetterInWord(item?.type ?? '')}
                  </Table.Cell>
                  <Table.Cell className="text-destructive w-25">
                    Delete
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Root>
        )}
      </Modal.Body>
      <Modal.Footer
        cancel={{
          variant: 'secondary',
          children: 'Cancel',
          onClick: closeModal,
        }}
        confirm={{
          variant: 'error',
          children: 'Delete',
          onClick: onDeleteConfirm,
        }}
      />
    </Modal.Layout>
  );
};
